// Pure mapping logic for editing an action's payment.request.send step through the
// BatchTemplateRequestModal: seeding the modal from the step's payload, and building
// the accept call's amendments (stepId -> full replacement payload) from the edited
// form values. No React here — SmartNoteModal consumes this.
//
// The step payload is the action-service's snake_case shape, schema-validated with
// additionalProperties:false — so a replacement payload is always built by overlaying
// edits onto a clone of the original, never by inventing keys.

const PAYMENT_STEP_TYPE = "payment.request.send";
const CALENDAR_STEP_TYPE = "calendar.event.create";
const EMAIL_STEP_TYPE = "email.send";

// The single email.send step of a send-documents action (there is always exactly one). Its payload
// carries the recipient, model-composed subject/body, and the materialized S3 attachment_locations.
export function editableEmailStep(steps) {
  const sends = (steps ?? []).filter((s) => s.stepType === EMAIL_STEP_TYPE);
  if (sends.length !== 1) return null;
  const step = sends[0];
  return step.stepId && step.payload ? step : null;
}

// An email reaches at most this many To addresses: the schema-required recipient_email plus up to four
// additional_recipient_emails (email.send 1.2.0). Cc and Bcc each take up to five more (1.3.0).
export const MAX_EMAIL_RECIPIENTS = 5;
export const MAX_EMAIL_COPIES = 5;

// Every address an email.send step goes to, primary first. Empty when the step names none.
export function emailRecipients(payload) {
  const primary = payload?.recipient_email ? [payload.recipient_email] : [];
  return [...primary, ...emailAddressList(payload, "additional_recipient_emails")];
}

// An address-list field of the step payload (cc_emails, bcc_emails, …), empty when absent.
export function emailAddressList(payload, key) {
  return Array.isArray(payload?.[key]) ? payload[key].filter(Boolean) : [];
}

// Overlay an edited address list: written when it has entries, removed when emptied, so a payload never
// carries an empty list for a field an older step schema does not know.
function applyAddressList(payload, key, addresses, max) {
  if (!Array.isArray(addresses)) return;
  if (addresses.length > 0) payload[key] = addresses.slice(0, max);
  else delete payload[key];
}

// The accept call's amendments for an email: overlay the edited subject/body, the (possibly pruned)
// attachment locations and the recipient list onto the email.send step's original payload. The
// attachment keys are the durable S3 locations materialized upstream — we never invent them, only drop
// deselected ones. The first recipient rides recipient_email and the rest additional_recipient_emails;
// that key is written only when there are extras, so an unchanged single recipient still validates
// against a step created before the field existed. Cc and Bcc lists follow the same rule.
export function buildEmailAmendments({ steps, subject, title, body, attachmentLocations, recipients, cc, bcc }) {
  const step = editableEmailStep(steps);
  if (!step) return {};

  const next = { ...step.payload };
  next.subject = subject ?? next.subject;
  // The heading is its own field, edited independently of the subject.
  next.title = title ?? next.title;
  next.body = body ?? next.body ?? "";
  if (Array.isArray(attachmentLocations)) next.attachment_locations = attachmentLocations;
  if (Array.isArray(recipients) && recipients.length > 0) {
    const [primary, ...additional] = recipients.slice(0, MAX_EMAIL_RECIPIENTS);
    next.recipient_email = primary;
    applyAddressList(next, "additional_recipient_emails", additional, MAX_EMAIL_RECIPIENTS - 1);
  }
  applyAddressList(next, "cc_emails", cc, MAX_EMAIL_COPIES);
  applyAddressList(next, "bcc_emails", bcc, MAX_EMAIL_COPIES);

  return { [step.stepId]: next };
}

// The Edit affordance exists only when the action carries exactly ONE payment request
// send — an instant request or a promise-to-pay. A plan's multiple instalment sends
// are deliberately not editable this way.
export function editablePaymentStep(steps) {
  const sends = (steps ?? []).filter((s) => s.stepType === PAYMENT_STEP_TYPE);
  if (sends.length !== 1) return null;
  const step = sends[0];
  return step.stepId && step.payload ? step : null;
}

// A statement send: semantically a document send that travels as a payment request —
// the statement is attached, no amount is named, and no invoice bucket was chosen
// (the backend normalises these to invoices_requested "None"). Drives the
// document-send wording where the payment-request wording would mislead.
export function isStatementStep(payload) {
  return (
    payload?.include_pdf_statement === true &&
    payload?.amount_requested == null &&
    (payload?.invoices_requested == null || payload?.invoices_requested === "None")
  );
}

// The proposed recipient(s) a suggestion will send to, shown on the main card only
// (the step rows never show contact details). Action-backed work reads the FIRST
// sending step — the backend stamps every send from the same customer record, so
// step [0] speaks for a plan's instalments too — with the copies the backend settled
// onto its cc_emails / bcc_emails. An email reads the requestConfig the backend
// stamped: every To address joined, plus any Cc and Bcc lists (emails have no SMS
// channel). Null when the suggestion sends nothing or no contact is known.
export function sendingToContacts(params) {
  const list = (addresses) => (Array.isArray(addresses) && addresses.length > 0 ? addresses.join(", ") : null);
  const sends = (params?.steps ?? []).filter((s) => s.stepType === PAYMENT_STEP_TYPE);
  if (sends.length > 0) {
    const payload = sends[0].payload ?? {};
    const email = payload.recipient_email || null;
    const phone = payload.recipient_phone_number || null;
    return email || phone
      ? { email, phone, cc: list(payload.cc_emails), bcc: list(payload.bcc_emails) }
      : null;
  }
  const config = params?.requestConfig;
  if (!config?.email) return null;
  return {
    email: [config.email, ...(config.additionalEmails ?? [])].join(", "),
    phone: null,
    cc: list(config.cc),
    bcc: list(config.bcc),
  };
}

// A StructuredV1 email_body is the PascalCase JSON the messaging service renders
// (Title/MainText/ActionArea) — the shape a template-resolved send carries. Null for
// any other format or an unparsable body, so those keep the raw-text seeding.
export function structuredEmailBody(payload) {
  if (payload?.email_content_format !== "StructuredV1") return null;
  try {
    const parsed = JSON.parse(payload.email_body ?? "");
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

// A scheduled specific-invoices send carries no amount_requested (payment-service resolves it when
// it sends), so the only figure on the step is the email headline intel composed from today's total.
function scheduledSpecificInvoicesAmount(invoicesRequested, structured) {
  if (invoicesRequested !== "SpecificInvoices") return null;
  const amount = Number(String(structured?.ActionArea?.Amount ?? "").replace(/[^0-9.-]/g, ""));
  return amount > 0 ? amount : null;
}

// Seed the modal from the step payload, in the flat requestConfig shape
// configToTemplate already consumes. A template-resolved send carries its wording as
// StructuredV1 JSON in email_body — parsed here so the editor seeds the real title,
// main text, action title and button text (and the template's name) instead of empty
// fields; a plain send seeds email_body as raw main text, as before.
export function stepToRequestConfig(payload) {
  if (!payload) return null;
  const invoicesRequested =
    payload.invoices_requested ??
    (payload.amount_requested != null ? "Custom" : "None");
  const structured = structuredEmailBody(payload);

  return {
    name: payload.message_template_name ?? undefined,
    invoicesRequested,
    customAmount: payload.amount_requested ?? scheduledSpecificInvoicesAmount(invoicesRequested, structured),
    mandatory: payload.invoices_requested_are_mandatory ?? false,
    attachPdfStatement: payload.include_pdf_statement ?? false,
    paymentProfileId: payload.payment_profile_id ?? null,
    allowCardPayment: payload.allow_card_payment ?? false,
    // recipient_email is schema-required, so email is always on; SMS rides alongside.
    emailEnabled: !!payload.recipient_email,
    smsEnabled: !!payload.recipient_phone_number,
    email: structured
      ? {
          subject: payload.email_subject ?? "",
          title: structured.Title ?? "",
          mainText: structured.MainText ?? "",
          actionTitle: structured.ActionArea?.Title ?? "",
          buttonText: structured.ActionArea?.ButtonText ?? "",
        }
      : {
          subject: payload.email_subject ?? "",
          mainText: payload.email_body ?? "",
        },
    sms: { text: payload.message ?? "" },
    // The step's actual recipients — the confirm step seeds its editable contact fields
    // from these, so a user-stated contact survives an edit round-trip instead of being
    // reset to the customer record's details.
    contactEmail: payload.recipient_email ?? null,
    contactPhone: payload.recipient_phone_number ?? null,
    // The copies the backend settled onto the send; the confirm step edits them as pills.
    cc: emailAddressList(payload, "cc_emails"),
    bcc: emailAddressList(payload, "bcc_emails"),
  };
}

// The same recipient resolution sendBatch applies per row: a channel contributes its
// contact only when enabled and valid.
function resolveRecipients(strategy, account) {
  return {
    email: strategy?.email && account && !account.emailError ? account.email : null,
    phone: strategy?.sms && account && !account.phoneError ? account.phone : null,
  };
}

// The accept call's amendments: { [stepId]: full replacement payload }. Overlays the
// edited form values onto the payment step's original payload; when the amount
// changed, also rewrites any calendar step title that names the old amount so the
// calendar entry the user approved stays truthful. Edited Cc/Bcc lists overlay
// cc_emails / bcc_emails (written only when non-empty, like the email's); an
// unedited list is left as the backend settled it.
export function buildAmendments({ steps, values, strategy, account, cc, bcc }) {
  const paymentStep = editablePaymentStep(steps);
  if (!paymentStep) return {};

  const original = paymentStep.payload;
  const next = { ...original };

  if (values.invoicesRequested === "Custom") {
    next.amount_requested = Number(values.customAmount);
    delete next.invoices_requested;
    delete next.specific_invoice_numbers;
    delete next.allow_additional_invoices;
    delete next.invoices_requested_are_mandatory;
  } else {
    next.invoices_requested = values.invoicesRequested;
    next.invoices_requested_are_mandatory = !!values.mandatory;
    delete next.amount_requested;
    if (values.invoicesRequested !== "SpecificInvoices") {
      delete next.specific_invoice_numbers;
    }
  }

  next.include_pdf_statement = !!values.attachPdfStatement;
  next.email_subject = values.emailSubject ?? original.email_subject;

  // A StructuredV1 body round-trips as JSON: the edited fields overlay the original
  // (PascalCase, the casing messaging deserializes), and anything the editor does not
  // expose — ActionArea.Description, Amount — is preserved verbatim.
  const structured = structuredEmailBody(original);
  if (structured) {
    next.email_body = JSON.stringify({
      ...structured,
      Title: values.emailTitle ?? structured.Title ?? "",
      MainText: values.emailMainText ?? structured.MainText ?? "",
      ActionArea: {
        ...structured.ActionArea,
        Title: values.emailActionTitle ?? structured.ActionArea?.Title ?? "",
        ButtonText: values.emailButtonText ?? structured.ActionArea?.ButtonText ?? "",
      },
    });
  } else {
    next.email_body = values.emailMainText ?? original.email_body ?? "";
  }
  next.allow_card_payment = !!values.allowCardPayment;
  if (values.paymentProfileId) next.payment_profile_id = values.paymentProfileId;
  else delete next.payment_profile_id;

  const recipients = resolveRecipients(strategy, account);
  // At least one channel must ride (schema v1.0.3): dropping the email is only honoured
  // when a phone replaces it — an SMS-only send, whose payload carries no email fields.
  // With no usable channel at all, fall back to the original email rather than compose
  // an unsendable payload.
  if (recipients.email) {
    next.recipient_email = recipients.email;
    applyAddressList(next, "cc_emails", cc, MAX_EMAIL_COPIES);
    applyAddressList(next, "bcc_emails", bcc, MAX_EMAIL_COPIES);
  } else if (recipients.phone) {
    // Copies ride the email channel: a text-only send carries none.
    delete next.recipient_email;
    delete next.email_subject;
    delete next.email_body;
    delete next.cc_emails;
    delete next.bcc_emails;
  } else {
    next.recipient_email = original.recipient_email;
    applyAddressList(next, "cc_emails", cc, MAX_EMAIL_COPIES);
    applyAddressList(next, "bcc_emails", bcc, MAX_EMAIL_COPIES);
  }
  if (recipients.phone) {
    next.recipient_phone_number = recipients.phone;
    next.message = values.smsText ?? original.message ?? "";
  } else {
    delete next.recipient_phone_number;
    delete next.message;
  }

  const amendments = { [paymentStep.stepId]: next };

  const calendar = syncedCalendarSteps(
    steps,
    original.amount_requested,
    next.amount_requested,
  );
  for (const [stepId, payload] of calendar) amendments[stepId] = payload;

  return amendments;
}

// Best-effort calendar-title sync when the amount changed: substitute the old amount
// (in the formats the model plausibly wrote it) with the new one. A title that
// doesn't name the old amount is left alone — never guess.
function syncedCalendarSteps(steps, oldAmount, newAmount) {
  if (
    typeof oldAmount !== "number" ||
    typeof newAmount !== "number" ||
    oldAmount === newAmount
  ) {
    return [];
  }

  const newForms = amountForms(newAmount);
  const candidates = amountForms(oldAmount).map((form, i) => ({
    // Digit/separator boundaries so a bare "400" never matches inside "1400".
    pattern: new RegExp(`(?<![\\d,.])${escapeRegExp(form)}(?![\\d,.])`, "g"),
    to: newForms[i],
  }));

  const synced = [];
  for (const step of steps ?? []) {
    if (step.stepType !== CALENDAR_STEP_TYPE || !step.stepId) continue;
    const title = step.payload?.title;
    if (typeof title !== "string") continue;

    const match = candidates.find((c) => title.match(c.pattern));
    if (!match) continue;

    synced.push([
      step.stepId,
      { ...step.payload, title: title.replace(match.pattern, match.to) },
    ]);
  }
  return synced;
}

// The written forms an amount plausibly appears in, most specific first so
// "1,500.00" wins over "1,500" before a bare "1500".
function amountForms(amount) {
  return [
    amount.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    amount.toLocaleString("en-GB"),
    String(amount),
  ];
}

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
